/**
 * Centralized session installation & destruction.
 *
 * All login paths (Email OTP user, Email OTP admin, Google OAuth, Apple OAuth)
 * funnel through `installSession`. All sign-out paths funnel through `destroySession`.
 *
 * Guarantees:
 *  - Single, consistent localStorage shape per bucket (`admin_*` vs `user_*`)
 *  - Opposite bucket is always cleared (no token bleed across roles)
 *  - Validation cache pre-populated synchronously to avoid post-login flicker
 *  - HttpOnly cookie set best-effort (cross-origin tolerant)
 *  - Sign-out clears caches synchronously BEFORE async revocation,
 *    so no other tab can read a "valid" session post-signout
 */

import { setValidationCache, clearValidationCache, type TokenValidationResult } from '@/hooks/useTokenValidation';
import { setAuthCookie, clearAuthCookie } from '@/utils/cookies';
import { authLogger } from '@/utils/authLogger';

export type SessionType = 'user' | 'admin';

export interface InstallSessionInput {
  token: string;
  type: SessionType;
  email: string;
  /** Optional profile snapshot returned by the auth edge function */
  profile?: {
    id?: string;
    email?: string;
    full_name?: string | null;
    subscription_tier?: string | null;
    payment_status?: string | null;
  } | null;
  /** Whether session should be remembered across browser restarts */
  remember?: boolean;
  /** Roles array for elevated-access users (admins) */
  roles?: string[];
  /** isAdmin flag (mirrors useTokenValidation cache shape) */
  isAdmin?: boolean;
}

const BUCKETS: SessionType[] = ['user', 'admin'];

function clearBucket(bucket: SessionType) {
  try {
    localStorage.removeItem(`${bucket}_auth_token`);
    localStorage.removeItem(`${bucket}_auth_type`);
    localStorage.removeItem(`${bucket}_auth_email`);
    localStorage.removeItem(`${bucket}_remember_me`);
  } catch {}
}

/**
 * Install a fresh session. Idempotent within a single tab — calling twice
 * with the same token simply re-asserts the same state.
 */
export function installSession(input: InstallSessionInput): void {
  const { token, type, email, profile, remember, roles, isAdmin } = input;

  // 1. Wipe the OPPOSITE bucket so role bleed is impossible
  const opposite: SessionType = type === 'admin' ? 'user' : 'admin';
  clearBucket(opposite);
  clearValidationCache(opposite);

  // 2. Write our bucket
  try {
    localStorage.setItem(`${type}_auth_token`, token);
    localStorage.setItem(`${type}_auth_type`, type);
    localStorage.setItem(`${type}_auth_email`, email);
    if (remember !== undefined) {
      localStorage.setItem(`${type}_remember_me`, remember ? 'true' : 'false');
    }
    if (roles && type === 'admin') {
      localStorage.setItem('user_roles', JSON.stringify(roles));
      localStorage.setItem('roles_fetched_at', Date.now().toString());
      localStorage.setItem('sirppi_view_mode', 'admin');
    }
    // Single, unified fresh-login marker (replaces fresh_login_grace_at + fresh_admin_login)
    localStorage.setItem('fresh_login_grace_at', Date.now().toString());
    if (type === 'admin') {
      // Kept for backward compat with AccessGate consumers
      localStorage.setItem('fresh_admin_login', Date.now().toString());
    }
    authLogger.tokenStored(type, email);
  } catch (err) {
    authLogger.error('installSession: localStorage write failed', { error: String(err) });
  }

  // 3. Pre-populate validation cache so the first useTokenValidation render is instant
  const cacheResult: TokenValidationResult = {
    isValid: true,
    userEmail: email,
    userName: profile?.full_name ?? null,
    paymentStatus: profile?.subscription_tier ?? profile?.payment_status ?? 'free',
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    usesRemaining: null,
    failureReason: null,
    isAdmin: !!isAdmin,
    profileId: profile?.id ?? null,
  };
  setValidationCache(type, cacheResult);

  // 4. Notify listeners (AuthContext) immediately
  try {
    window.dispatchEvent(new CustomEvent('auth:signin', { detail: { type, email } }));
  } catch {}

  // 5. Best-effort HttpOnly cookie (non-blocking — works only same-origin)
  setAuthCookie(token, type, email, remember).catch(() => {
    // Expected on cross-origin / iframe contexts
  });
}

/**
 * Destroy the current session. Synchronously clears in-memory + storage state,
 * THEN best-effort revokes server-side. Callers can `await` if they need the
 * server-side cleanup to settle (e.g. before navigating to /login).
 */
export async function destroySession(opts: { revokeServerSide?: boolean } = {}): Promise<void> {
  const { revokeServerSide = true } = opts;

  // Capture token + identifying context before nuking storage
  let token: string | null = null;
  let priorEmail: string | null = null;
  let priorBucket: SessionType | null = null;
  try {
    const adminTok = localStorage.getItem('admin_auth_token');
    const userTok = localStorage.getItem('user_auth_token');
    token = adminTok || userTok;
    priorBucket = adminTok ? 'admin' : userTok ? 'user' : null;
    priorEmail = priorBucket
      ? localStorage.getItem(`${priorBucket}_auth_email`)
      : null;
  } catch {}

  // Telemetry: log destruction intent (fire-and-forget, before clearing storage
  // so profile_id is still available)
  try {
    const { logEvent } = await import('@/utils/telemetry');
    logEvent('auth.logout', {
      detail: { bucket: priorBucket, hadToken: !!token, revokeServerSide },
    });
  } catch {}

  // 1. SYNCHRONOUS: clear validation cache for both buckets
  clearValidationCache();

  // 2. SYNCHRONOUS: clear localStorage for both buckets + ancillary keys
  for (const b of BUCKETS) clearBucket(b);
  try {
    localStorage.removeItem('user_roles');
    localStorage.removeItem('roles_fetched_at');
    localStorage.removeItem('sirppi_view_mode');
    localStorage.removeItem('fresh_login_grace_at');
    localStorage.removeItem('fresh_admin_login');
    localStorage.removeItem('sirppi_profile_id');
    // Per-user studio caches that can otherwise leak the previous user's
    // resume into the next user's Analysis page.
    localStorage.removeItem('studio_resume_path');
    localStorage.removeItem('studio_resume_analysis');
    sessionStorage.removeItem('oauth_bridge_inflight');
    sessionStorage.removeItem('oauth_initiated');
    sessionStorage.removeItem('ui_payment_flow_completed');
    sessionStorage.removeItem('ui_whitelist_flow_completed');
    // Defensive: nuke any leftover Supabase auth tokens so the JS client
    // doesn't auto-refresh them and re-bridge a stale OAuth identity.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && (k.startsWith('sb-') || k.includes('-auth-token'))) {
        localStorage.removeItem(k);
      }
    }
  } catch {}

  // 2b. SYNCHRONOUS: nuke the React Query cache so no other tab/component
  // can read PII (profile, careers, payments) under the next user's identity.
  let cacheCleared = false;
  try {
    const qc = (window as any).__sirppiQueryClient;
    if (qc?.clear) {
      qc.clear();
      cacheCleared = true;
    }
  } catch {}

  // Telemetry: confirm cache cleared
  try {
    const { logEvent } = await import('@/utils/telemetry');
    logEvent('auth.logout_cache_cleared', {
      severity: cacheCleared ? 'info' : 'warn',
      detail: { cacheCleared, bucket: priorBucket },
    });
  } catch {}

  // 3. SYNCHRONOUS: tell the rest of the app
  try {
    window.dispatchEvent(new Event('auth:signout'));
  } catch {}

  if (!revokeServerSide) return;

  // 4. ASYNC best-effort cleanup
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    await Promise.allSettled([
      token ? (supabase.rpc as any)('revoke_token', { token_input: token }) : Promise.resolve(),
      token
        ? (supabase as any).from('session_token_cache').delete().eq('session_token', token)
        : Promise.resolve(),
      supabase.auth.signOut().catch(() => {}),
      clearAuthCookie(),
    ]);
  } catch (err) {
    authLogger.error('destroySession: server-side cleanup failed', { error: String(err) });
  }
}

/** Read the currently-installed session, if any. */
export function getSession(): { type: SessionType; token: string; email: string | null } | null {
  try {
    const adminToken = localStorage.getItem('admin_auth_token');
    if (adminToken) {
      return { type: 'admin', token: adminToken, email: localStorage.getItem('admin_auth_email') };
    }
    const userToken = localStorage.getItem('user_auth_token');
    if (userToken) {
      return { type: 'user', token: userToken, email: localStorage.getItem('user_auth_email') };
    }
  } catch {}
  return null;
}
